import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, Filter, Mail, Phone, Zap, MoreVertical,
  FileText, Loader2, Globe, Layers,
  Sparkles, Download, Folder, ChevronRight,
  Target, X, Trash2, FolderOpen, Check
} from 'lucide-react';
import api from '../api';
import { createCustomDraft } from '../../email-intel/api';
import { cn } from '@/lib/utils';
import WebsiteIntelligenceModal from '../components/WebsiteIntelligenceModal';
import CompetitorIntelligenceModal from '../components/CompetitorIntelligenceModal';
import ModernCompetitorDashboard from '../components/competitors/ModernCompetitorDashboard';
import SalesAgentModal from '../components/SalesAgentModal';

// Chip-style multi-keyword search: press Enter to lock in a keyword and start
// typing the next one. A result must match every locked-in keyword (AND),
// plus whatever's currently being typed (live filtering as you type).
function useKeywordSearch() {
  const [draft, setDraft] = useState('');
  const [keywords, setKeywords] = useState([]);

  const addKeyword = () => {
    const val = draft.trim();
    if (val && !keywords.some((k) => k.toLowerCase() === val.toLowerCase())) {
      setKeywords((prev) => [...prev, val]);
    }
    setDraft('');
  };

  const removeKeyword = (kw) => setKeywords((prev) => prev.filter((k) => k !== kw));

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    } else if (e.key === 'Backspace' && !draft && keywords.length > 0) {
      setKeywords((prev) => prev.slice(0, -1));
    }
  };

  const clear = () => { setDraft(''); setKeywords([]); };

  const activeTerms = [...keywords, draft.trim()].filter(Boolean);

  const matches = (text) => {
    if (activeTerms.length === 0) return true;
    const lower = (text || '').toLowerCase();
    return activeTerms.every((term) => lower.includes(term.toLowerCase()));
  };

  return { draft, setDraft, keywords, addKeyword, removeKeyword, onKeyDown, clear, activeTerms, matches };
}

// Light normalization so folder grouping doesn't split on trivial variants like
// "school" vs "schools" or "company" vs "companies". Display text (folderName)
// still uses the raw value from the lead — only the grouping key is normalized.
function normalizeBusinessType(value) {
  const v = (value || '').trim().toLowerCase();
  if (!v) return v;
  if (v.endsWith('ies') && v.length > 3) return v.slice(0, -3) + 'y';
  if (v.endsWith('ses') && v.length > 3) return v.slice(0, -2);
  if (v.endsWith('s') && !v.endsWith('ss') && v.length > 1) return v.slice(0, -1);
  return v;
}

function KeywordSearchBox({ state, placeholder, className, inputClassName }) {
  return (
    <div className={className}>
      <div className="relative group">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={16} />
        <input
          type="text"
          placeholder={placeholder}
          value={state.draft}
          onChange={(e) => state.setDraft(e.target.value)}
          onKeyDown={state.onKeyDown}
          className={inputClassName}
        />
      </div>
      {state.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {state.keywords.map((kw) => (
            <span key={kw} className="inline-flex items-center gap-1 bg-indigo-500/10 text-indigo-400 text-[11px] font-bold pl-2 pr-1 py-1 rounded-lg">
              {kw}
              <button onClick={() => state.removeKeyword(kw)} className="p-0.5 rounded hover:bg-indigo-500/20 hover:text-rose-400 transition-colors" aria-label={`Remove keyword ${kw}`}>
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const folderSearch = useKeywordSearch();
  const leadSearchState = useKeywordSearch();
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [crmToggling, setCrmToggling] = useState(null);
  const [generatingProposal, setGeneratingProposal] = useState(null);
  const [analyzingWebsite, setAnalyzingWebsite] = useState(null);
  const [analyzingCompetitors, setAnalyzingCompetitors] = useState(null);
  const [intelligenceModalLead, setIntelligenceModalLead] = useState(null);
  const [competitorModalLead, setCompetitorModalLead] = useState(null);
  const [salesAgentModalLead, setSalesAgentModalLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [actionMenuLead, setActionMenuLead] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [detailedLead, setDetailedLead] = useState(null);
  const [sendingToDraft, setSendingToDraft] = useState(false);
  const [modernCompetitorModalLead, setModernCompetitorModalLead] = useState(null);

  const [activeFolder, setActiveFolder] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (!actionMenuLead) return;
    const closeMenu = () => { setActionMenuLead(null); setActionMenuPosition(null); };
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [actionMenuLead]);

  const fetchLeads = async (updatedLeadId) => {
    try {
      const response = await api.get('/leads/');
      setLeads(response.data);

      if (updatedLeadId) {
        const updated = response.data.find((l) => l._id === updatedLeadId);
        if (updated) {
          if (intelligenceModalLead?._id === updatedLeadId) setIntelligenceModalLead(updated);
          if (competitorModalLead?._id === updatedLeadId) setCompetitorModalLead(updated);
          if (salesAgentModalLead?._id === updatedLeadId) setSalesAgentModalLead(updated);
          if (detailedLead?._id === updatedLeadId) setDetailedLead(updated);
        }
      }
    } catch (err) {
      console.error('Failed to fetch leads', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeCompetitors = async (leadId) => {
    setAnalyzingCompetitors(leadId);
    try {
      await api.post(`/leads/${leadId}/analyze-competitors`);
      fetchLeads(leadId);
    } catch (err) {
      console.error('Competitor analysis failed', err);
      alert("Failed to analyze competitors. Make sure your lead has valid coordinates/address.");
    } finally {
      setAnalyzingCompetitors(null);
    }
  };

  const handleGenerateProposal = async (leadId) => {
    setGeneratingProposal(leadId);
    try {
      await api.post(`/leads/${leadId}/generate-proposal`);
      alert("Proposal Generated Successfully!");
      fetchLeads(leadId);
    } catch (err) {
      console.error('Proposal generation failed', err);
      alert(`Proposal generation failed: ${err.response?.data?.detail || err.message || 'Please try again.'}`);
    } finally {
      setGeneratingProposal(null);
    }
  };

  const handleAnalyzeWebsite = async (leadId) => {
    setAnalyzingWebsite(leadId);
    try {
      await api.post(`/leads/${leadId}/analyze-website`);
      fetchLeads(leadId);
    } catch (err) {
      console.error('Website analysis failed', err);
      alert("Failed to analyze website");
    } finally {
      setAnalyzingWebsite(null);
    }
  };

  const handleDownloadCSV = (customLeads, prefix) => {
    if (!customLeads || customLeads.length === 0) return;

    const headers = ["Business Name", "Address", "City", "Phone", "Email", "Website", "Status", "Digital Health Score", "Missing Features"];
    const csvContent = [
      headers.join(","),
      ...customLeads.map((l) => [
        `"${(l.name || '').replace(/"/g, '""')}"`,
        `"${(l.address || '').replace(/"/g, '""')}"`,
        `"${(l.city || '').replace(/"/g, '""')}"`,
        `"${(l.phone || '').replace(/"/g, '""')}"`,
        `"${(l.email || '').replace(/"/g, '""')}"`,
        `"${(l.website || '').replace(/"/g, '""')}"`,
        `"${(l.status || '').replace(/"/g, '""')}"`,
        `"${l.digital_health_score || 0}"`,
        `"${(l.gap_labels || []).join(', ')}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = prefix ? `${prefix}_leads_${new Date().toISOString().split('T')[0]}.csv` : `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/leads/${leadId}`);
      if (detailedLead?._id === leadId) setDetailedLead(null);
      fetchLeads();
    } catch (err) {
      console.error('Failed to delete lead', err);
    }
    setActionMenuLead(null);
  };

  const handleToggleCrmPipeline = async (leadId) => {
    setCrmToggling(leadId);
    try {
      const response = await api.patch(`/leads/${leadId}/crm-pipeline`);
      const { added_to_crm } = response.data;
      setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, added_to_crm } : l)));
    } catch (err) {
      console.error('Failed to toggle CRM pipeline', err);
      alert('Failed to update CRM Pipeline status: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCrmToggling(null);
    }
  };

  const handleDeleteFolder = async (e, folderKey, folderName, folderLeads) => {
    e.stopPropagation();
    if (!window.confirm(`Delete folder "${folderName}" and all ${folderLeads.length} lead(s) inside it? This cannot be undone.`)) return;
    setDeletingFolder(folderKey);
    try {
      await api.post('/leads/bulk-delete', { lead_ids: folderLeads.map((l) => l._id) });
      if (activeFolder === folderKey) setActiveFolder(null);
      fetchLeads();
    } catch (err) {
      console.error('Failed to delete folder', err);
      alert('Failed to delete folder: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingFolder(null);
    }
  };

  // Copies this lead's generated proposal into Email Intel as an editable
  // draft instead of sending it straight out via SMTP (the old "Send
  // Email" behavior) - goes through the same "+ Custom email" endpoint
  // Email Intel's own compose button uses, since these leads live in
  // leads_finder's own table with no lead_engine company_id to link a
  // normal draft to. `email` is prefilled but left editable in the draft
  // (this lead may have none on file - "No email address" is common here),
  // so it's no longer required just to send this to draft.
  const handleSendToDraft = async () => {
    if (!detailedLead) return;
    setSendingToDraft(true);
    try {
      await createCustomDraft({
        email: detailedLead.email || undefined,
        subject: `Proposal for ${detailedLead.name}`,
        body: detailedLead.proposals?.[0]?.content || '',
        company_name: detailedLead.name,
      });
      alert("Sent to Draft — find it under Email Intel to review or customize before sending.");
    } catch (err) {
      alert("Failed to send to draft: " + (err.response?.data?.detail || err.message));
    } finally {
      setSendingToDraft(false);
    }
  };

  const groupedLeads = leads.reduce((acc, lead) => {
    const target = (lead.business_type || '').trim();
    const location = (lead.city || '').trim();
    const folderKey = target && location ? `${normalizeBusinessType(target)}::${location.toLowerCase()}` : 'other';
    const folderName = target && location ? `${target} in ${location}` : 'Other Leads';

    if (!folderSearch.matches(folderName)) return acc;

    if (!acc[folderKey]) acc[folderKey] = { name: folderName, target, location, leads: [] };
    acc[folderKey].leads.push(lead);
    return acc;
  }, {});

  const visibleFolders = Object.entries(groupedLeads);
  console.log(visibleFolders, 'visibleFolders');

  useEffect(() => {
    if (activeFolder && !groupedLeads[activeFolder]) {
      setActiveFolder(null);
    }
  }, [groupedLeads, activeFolder]);

  const activeLeads = activeFolder ? groupedLeads[activeFolder]?.leads || [] : [];

  const filteredActiveLeads = activeLeads.filter((l) => {
    const matchesSearch = leadSearchState.matches(`${l.name || ''} ${l.address || ''}`);
    const matchesStatus = statusFilter === 'All' || l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Lead': return "bg-blue-500/10 text-blue-400 border-blue-500/20";
      case 'Contacted': return "bg-orange-500/10 text-orange-400 border-orange-500/20";
      case 'Proposal Sent': return "bg-purple-500/10 text-purple-400 border-purple-500/20";
      case 'Proposal Declined': return "bg-rose-500/10 text-rose-400 border-rose-500/20";
      case 'Closed Won': return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
      default: return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    }
  };

  const getHealthColor = (score) => {
    if (score > 50) return "text-red-400 bg-red-500/10 border-red-500/20";
    if (score > 20) return "text-orange-400 bg-orange-500/10 border-orange-500/20";
    return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
  };

  return (
    <div className="flex h-full relative overflow-hidden bg-slate-950">

      {/* LEFT PANE: FOLDERS SIDEBAR */}
      <div className="w-[320px] flex-shrink-0 border-r border-white/[0.06] bg-slate-950/60 flex flex-col h-full z-10">
        <div className="p-6 border-b border-white/[0.06]">
          <h2 className="text-xl font-bold text-white mb-4">Categories</h2>
          <KeywordSearchBox
            state={folderSearch}
            placeholder="Search folders... (Enter to add)"
            inputClassName="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-2.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-white"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-3">
              <Loader2 className="animate-spin text-indigo-400" size={24} />
              <p className="text-xs text-slate-500 font-medium">Loading...</p>
            </div>
          ) : visibleFolders.length === 0 ? (
            <div className="text-center py-10 px-4 text-slate-500 text-sm space-y-3">
              {Object.keys(groupedLeads).length === 0 && leads.length === 0 ? (
                <p>No categories found. Scan an area to generate leads.</p>
              ) : (
                <>
                  <p>No folders match "{folderSearch.activeTerms.join('", "')}" — your leads are still saved, they're just hidden by the search.</p>
                  <button onClick={() => folderSearch.clear()} className="text-xs font-bold text-indigo-400 hover:underline">
                    Clear search
                  </button>
                </>
              )}
            </div>
          ) : (
            visibleFolders.map(([folderKey, folder]) => {
              const isActive = activeFolder === folderKey;
              console.log('folderlenth', folder.leads.length);

              return (
                <div key={folderKey}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveFolder(folderKey)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveFolder(folderKey); } }}
                  className={cn("w-full flex items-center justify-between p-3 rounded-xl text-left transition-all group cursor-pointer",
                    isActive ? "bg-indigo-500/10 text-indigo-400 ring-1 ring-indigo-500/20" : "hover:bg-white/[0.04] text-slate-300")}>
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className={cn("flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all", isActive ? "bg-indigo-500 text-white shadow-md scale-105" : "bg-white/[0.05] text-slate-500 group-hover:text-indigo-400 group-hover:bg-indigo-500/10")}>
                      {isActive ? <FolderOpen size={18} /> : <Folder size={18} />}
                    </div>
                    <div className="overflow-hidden">
                      <p className={cn("text-sm font-bold truncate", isActive ? "text-indigo-400" : "text-white group-hover:text-indigo-400")}>{folder.name === 'manual in manual' ? 'Manual' : folder.name}</p>
                      <p className={cn("text-[11px] font-semibold mt-0.5", isActive ? "text-indigo-400/70" : "text-slate-500")}>{folder.leads.length} Leads</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => handleDeleteFolder(e, folderKey, folder.name, folder.leads)}
                      disabled={deletingFolder === folderKey}
                      aria-label={`Delete folder ${folder.name}`}
                      title="Delete folder"
                      className="p-1.5 rounded-lg text-slate-500 opacity-0 group-hover:opacity-100 hover:text-rose-400 hover:bg-rose-500/10 transition-all disabled:opacity-50"
                    >
                      {deletingFolder === folderKey ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                    {isActive && <ChevronRight size={18} className="text-indigo-400" />}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT PANE: MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto p-8">
        {!activeFolder ? (
          <div className="h-full flex flex-col items-center justify-center py-32 border-2 border-dashed border-white/[0.08] rounded-3xl bg-white/[0.02] max-w-3xl mx-auto">
            <div className="w-20 h-20 bg-white/[0.03] border border-white/[0.06] rounded-2xl flex items-center justify-center mb-6 rotate-3">
              <FolderOpen className="text-slate-600" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Select a Category</h3>
            <p className="text-slate-400 max-w-sm text-center text-sm leading-relaxed">
              Choose a folder from the left sidebar to view, manage, and engage with its specific leads.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end mb-8 gap-4 bg-white/[0.02] p-6 rounded-2xl border border-white/[0.06]">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400"><FolderOpen size={16} /></div>
                  <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Active Category</span>
                </div>
                <h1 className="text-3xl font-bold tracking-tight mb-1 text-white">{groupedLeads[activeFolder]?.name}</h1>
                <p className="text-slate-400 font-medium text-sm">{activeLeads.length} total leads found in this location.</p>
              </div>

              <div className="flex items-center gap-3 w-full xl:w-auto">
                <KeywordSearchBox
                  state={leadSearchState}
                  placeholder="Search inside category... (Enter to add)"
                  className="flex-1 xl:w-64"
                  inputClassName="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-2 pl-9 pr-4 text-sm outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-white"
                />

                <div className="relative">
                  <button onClick={() => setShowFilters(!showFilters)}
                    className={cn("px-4 py-2 rounded-xl border flex items-center gap-2 transition-all font-bold text-sm",
                      showFilters ? "bg-white/[0.08] border-white/[0.1] text-white" : "bg-white/[0.02] border-white/[0.08] text-slate-300 hover:bg-white/[0.05]")}>
                    <Filter size={16} /> Filter
                  </button>
                  {showFilters && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-900 border border-white/[0.08] rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-slate-500 px-2 py-1 uppercase tracking-wider">Status Filter</span>
                      {['All', 'Lead', 'Contacted', 'Proposal Sent', 'Proposal Declined', 'Closed'].map((status) => (
                        <button key={status} onClick={() => { setStatusFilter(status); setShowFilters(false); }}
                          className={cn("text-left px-3 py-2 rounded-lg text-sm font-bold transition-colors",
                            statusFilter === status ? "text-indigo-400 bg-indigo-500/10" : "text-slate-400 hover:bg-white/[0.04]")}>
                          {status}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button onClick={() => handleDownloadCSV(activeLeads, activeFolder.replace(/\s+/g, '_').toLowerCase())}
                  className="px-4 py-2 rounded-xl border border-white/[0.08] bg-white/[0.02] text-slate-300 hover:bg-white/[0.05] flex items-center gap-2 transition-all font-bold text-sm">
                  <Download size={16} /> Export
                </button>
              </div>
            </div>

            {filteredActiveLeads.length === 0 ? (
              <div className="text-center py-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
                <p className="text-slate-400 font-medium">No leads match your search criteria.</p>
              </div>
            ) : (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-white/[0.03] border-b border-white/[0.06] text-xs uppercase tracking-wider text-slate-500 font-bold">
                        <th className="px-6 py-4">Lead Name</th>
                        <th className="px-6 py-4">CRM</th>
                        <th className="px-6 py-4">Contact</th>
                        <th className="px-6 py-4">Status & Health</th>
                        <th className="px-6 py-4">Identified Gaps</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {filteredActiveLeads.map((lead) => (
                        <tr key={lead._id} className={cn("group/row transition-colors hover:bg-white/[0.03]", detailedLead?._id === lead._id && "bg-indigo-500/5")}>
                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col cursor-pointer" onClick={() => setDetailedLead(lead)}>
                              <span className="font-bold text-white text-sm mb-1 hover:text-indigo-400 transition-colors">{lead.name}</span>
                              <span className="text-xs text-slate-500 line-clamp-1 max-w-[250px]">{lead.address}</span>
                            </div>
                          </td>

                          <td className="px-6 py-4 align-top">
                            <button
                              onClick={() => handleToggleCrmPipeline(lead._id)}
                              disabled={crmToggling === lead._id}
                              className={cn(
                                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all disabled:opacity-50 whitespace-nowrap",
                                lead.added_to_crm
                                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20"
                                  : "bg-white/[0.03] text-slate-400 border-white/[0.08] hover:bg-indigo-500/10 hover:text-indigo-400 hover:border-indigo-500/20"
                              )}
                            >
                              {crmToggling === lead._id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : lead.added_to_crm ? (
                                <Check size={12} />
                              ) : null}
                              {lead.added_to_crm ? 'Added' : 'Add to CRM'}
                            </button>
                          </td>

                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col space-y-2 text-xs text-slate-400">
                              <div className="flex items-center gap-2">
                                <Phone size={14} className={lead.phone ? "text-indigo-400" : "text-slate-600"} />
                                <span className="truncate max-w-[150px] font-medium">{lead.phone || "No phone"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail size={14} className={lead.email ? "text-indigo-400" : "text-slate-600"} />
                                <span className="truncate max-w-[150px] font-medium">{lead.email || "No email"}</span>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col items-start gap-2">
                              <span className={cn("px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border", getStatusColor(lead.status))}>{lead.status}</span>
                              <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-bold border flex items-center gap-1", getHealthColor(lead.digital_health_score))}>
                                <Zap size={10} /> {lead.digital_health_score} Score
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {lead.gap_labels?.slice(0, 3).map((gap) => (
                                <span key={gap} className="px-2 py-1 rounded bg-white/[0.04] text-slate-400 text-[10px] font-semibold border border-white/[0.06]">{gap}</span>
                              ))}
                              {lead.gap_labels?.length > 3 && (
                                <span className="px-2 py-1 rounded bg-white/[0.02] text-slate-500 text-[10px] font-semibold border border-white/[0.06]">+{lead.gap_labels.length - 3}</span>
                              )}
                            </div>
                          </td>

                          <td className="px-6 py-4 align-top text-right">
                            <div className="flex justify-end items-center gap-2">
                              <button onClick={() => setDetailedLead(lead)}
                                className="text-xs font-bold text-slate-300 bg-white/[0.03] border border-white/[0.08] hover:bg-indigo-500/10 hover:text-indigo-400 hover:border-indigo-500/20 px-3 py-1.5 rounded-lg transition-colors">
                                Details
                              </button>

                              <div className="relative">
                                <button onClick={(e) => {
                                    e.stopPropagation();
                                    if (actionMenuLead === lead._id) {
                                      setActionMenuLead(null);
                                      setActionMenuPosition(null);
                                    } else {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setActionMenuPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                                      setActionMenuLead(lead._id);
                                    }
                                  }}
                                  aria-label="More lead actions"
                                  className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-400 hover:bg-indigo-500/10 transition-colors border border-transparent hover:border-indigo-500/20">
                                  <MoreVertical size={16} />
                                </button>

                                {actionMenuLead === lead._id && actionMenuPosition && createPortal(
                                  <>
                                    <div className="fixed inset-0 z-40" onClick={() => { setActionMenuLead(null); setActionMenuPosition(null); }} />
                                    <div
                                      style={{ position: 'fixed', top: actionMenuPosition.top, right: actionMenuPosition.right }}
                                      className="w-56 bg-slate-900 border border-white/[0.08] rounded-xl shadow-2xl z-50 p-1.5 flex flex-col gap-0.5">
                                      <p className="px-3 pt-1 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-500">Lead actions</p>
                                      <button onClick={() => { handleGenerateProposal(lead._id); setActionMenuLead(null); setActionMenuPosition(null); }} className="text-left px-3 py-2.5 rounded-lg text-sm text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 flex items-center gap-2 font-bold transition-colors">
                                        {generatingProposal === lead._id ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} className="text-orange-400" />} Generate Pitch
                                      </button>
                                      <div className="h-px bg-white/[0.06] my-1 mx-2" />
                                      <p className="px-3 pt-1 pb-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-500">Intelligence</p>
                                      <button onClick={() => { setIntelligenceModalLead(lead); setActionMenuLead(null); setActionMenuPosition(null); }} className="text-left px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/[0.05] hover:text-indigo-400 flex items-center gap-2 font-medium transition-colors">
                                        <Globe size={14} className="text-indigo-400" /> Website Intel
                                      </button>
                                      <button onClick={() => { setCompetitorModalLead(lead); setActionMenuLead(null); setActionMenuPosition(null); }} className="text-left px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/[0.05] hover:text-indigo-400 flex items-center gap-2 font-medium transition-colors">
                                        <Layers size={14} className="text-cyan-400" /> Competitor Intel
                                      </button>
                                      <button onClick={() => { setModernCompetitorModalLead(lead); setActionMenuLead(null); setActionMenuPosition(null); }} className="text-left px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/[0.05] hover:text-indigo-400 flex items-center gap-2 font-medium transition-colors">
                                        <Target size={14} className="text-orange-400" /> AI Pipeline
                                      </button>
                                      <button onClick={() => { setSalesAgentModalLead(lead); setActionMenuLead(null); setActionMenuPosition(null); }} className="text-left px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/[0.05] hover:text-indigo-400 flex items-center gap-2 font-medium transition-colors">
                                        <Sparkles size={14} className="text-amber-400" /> AI Sales Agent
                                      </button>
                                      <div className="h-px bg-white/[0.06] my-1 mx-2" />
                                      <button onClick={() => { handleDeleteLead(lead._id); setActionMenuLead(null); setActionMenuPosition(null); }} className="text-left px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 font-bold transition-colors">
                                        <Trash2 size={14} /> Delete Lead
                                      </button>
                                    </div>
                                  </>,
                                  document.body
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-3 border-t border-white/[0.06] bg-white/[0.02] flex justify-between items-center">
                  <span className="text-xs font-semibold text-slate-500">Showing {filteredActiveLeads.length} leads in {activeFolder}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* SLIDE OUT DRAWER */}
      {detailedLead && <div className="fixed inset-0 z-20" onClick={() => setDetailedLead(null)} />}
      <div className={cn("absolute right-0 top-0 h-full w-[500px] bg-slate-950 border-l border-white/[0.08] shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] flex flex-col z-30",
        detailedLead ? "translate-x-0" : "translate-x-full")}>
        {detailedLead && (
          <>
            <div className="p-6 border-b border-white/[0.06] flex items-start justify-between bg-slate-950/60">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-white/[0.03]", getStatusColor(detailedLead.status))}>{detailedLead.status}</span>
                  <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold border bg-white/[0.03]", getHealthColor(detailedLead.digital_health_score))}>Score: {detailedLead.digital_health_score}</span>
                </div>
                <h2 className="text-2xl font-bold text-white leading-tight">{detailedLead.name}</h2>
                <p className="text-sm text-slate-400 mt-1 font-medium">{detailedLead.address}</p>
              </div>
              <button onClick={() => setDetailedLead(null)} className="p-2 rounded-full hover:bg-white/[0.06] text-slate-500 hover:text-slate-300 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-950">

              <div className="bg-white/[0.02] rounded-2xl p-5 border border-white/[0.06]">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400"><Phone size={14} /></div>
                    <span className="text-sm font-bold text-slate-300">{detailedLead.phone || 'No phone number'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400"><Mail size={14} /></div>
                    <span className="text-sm font-bold text-slate-300">{detailedLead.email || 'No email address'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400"><Globe size={14} /></div>
                    {detailedLead.website ? (
                      <a href={detailedLead.website.startsWith('http') ? detailedLead.website : `https://${detailedLead.website}`} target="_blank" rel="noreferrer" className="text-sm font-bold text-purple-400 hover:underline">{detailedLead.website}</a>
                    ) : <span className="text-sm font-bold text-slate-500">No website</span>}
                  </div>
                </div>
              </div>

              <div className="bg-white/[0.02] rounded-2xl p-5 border border-white/[0.06]">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Identified Gaps</h3>
                <div className="flex flex-wrap gap-2">
                  {detailedLead.gap_labels && detailedLead.gap_labels.length > 0 ? (
                    detailedLead.gap_labels.map((gap, i) => (
                      <span key={i} className="px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs font-bold text-slate-300">{gap}</span>
                    ))
                  ) : <span className="text-slate-500 text-sm font-medium">No gaps detected.</span>}
                </div>
              </div>

              <div className="bg-white/[0.02] rounded-2xl border border-white/[0.06] flex flex-col h-auto min-h-[300px]">
                <div className="p-4 border-b border-white/[0.06] bg-white/[0.02] rounded-t-2xl flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><FileText size={14} /> AI Proposal</h3>
                  {detailedLead.proposals && detailedLead.proposals.length > 0 && (
                    <button onClick={handleSendToDraft} disabled={sendingToDraft}
                      className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50">
                      {sendingToDraft ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                      {sendingToDraft ? "Sending..." : "Send to Draft"}
                    </button>
                  )}
                </div>

                {detailedLead.proposals && detailedLead.proposals.length > 0 ? (
                  <div className="text-sm leading-relaxed whitespace-pre-wrap bg-white/[0.01] p-5 overflow-y-auto text-slate-300">
                    {detailedLead.proposals[0].content}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-white/[0.01] rounded-b-2xl">
                    <Zap size={32} className="text-amber-400 mb-3" />
                    <h4 className="font-bold text-white mb-1">No Proposal Yet</h4>
                    <p className="text-xs text-slate-500 mb-4 max-w-[200px]">Generate a customized pitch based on their digital gaps.</p>
                    <button onClick={() => handleGenerateProposal(detailedLead._id)} disabled={generatingProposal === detailedLead._id}
                      className="bg-white/[0.04] hover:bg-white/[0.07] text-white border border-white/[0.08] px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all">
                      {generatingProposal === detailedLead._id ? <Loader2 size={16} className="animate-spin text-indigo-400" /> : <Zap size={16} className="text-amber-400" />}
                      Generate Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <WebsiteIntelligenceModal isOpen={!!intelligenceModalLead} onClose={() => setIntelligenceModalLead(null)} intelligence={intelligenceModalLead?.website_intelligence || null} businessName={intelligenceModalLead?.name || ''} leadId={intelligenceModalLead?._id} onRefresh={() => fetchLeads(intelligenceModalLead?._id)} />
      <CompetitorIntelligenceModal isOpen={!!competitorModalLead} onClose={() => setCompetitorModalLead(null)} analysis={competitorModalLead?.competitor_analysis || null} businessName={competitorModalLead?.name || ''} leadId={competitorModalLead?._id} onRefresh={() => fetchLeads(competitorModalLead?._id)} prospectRating={competitorModalLead?.rating || 0} prospectReviews={competitorModalLead?.reviews_count || 0} prospectHasWebsite={!!competitorModalLead?.website} />
      {modernCompetitorModalLead && <ModernCompetitorDashboard isOpen={!!modernCompetitorModalLead} onClose={() => setModernCompetitorModalLead(null)} leadId={modernCompetitorModalLead._id} businessName={modernCompetitorModalLead.name} />}
      <SalesAgentModal isOpen={!!salesAgentModalLead} onClose={() => setSalesAgentModalLead(null)} leadId={salesAgentModalLead?._id} businessName={salesAgentModalLead?.name || ''} salesAgent={salesAgentModalLead?.sales_agent || null} generationStatus={salesAgentModalLead?.generation_status} onRefresh={() => fetchLeads(salesAgentModalLead?._id)} />
    </div>
  );
}
